import React, { useState, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Row, Col } from "reactstrap";
import { reportsApi } from "../../services/reportsServices";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Function to generate colors automatically
const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / count;
    const saturation = 65 + (i % 3) * 10; // Varies between 65–85%
    const lightness = 50 + (i % 2) * 5; // Varies between 50–55%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

const LaborCodeChart = ({ title = "Total hours per LC" }) => {
  const [chartData, setChartData] = useState(null);
  const statusId = 10;

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const result = await reportsApi.manHoursByLaborCode({ statusId });
        if (
          result &&
          result.data &&
          result.data &&
          result.data.laborCategories.length > 0
        ) {
          const formattedData = {
            labels: result.data.laborCategories.map(
              (item) => `${item.code} ${item.name}`
            ),
            datasets: [
              {
                data: result.data.laborCategories.map((item) =>
                  parseFloat(item.totalEstimatedHours)
                ),
                borderWidth: 0,
              },
            ],
          };
          setChartData(formattedData);
        } else {
          setChartData(null);
        }
      } catch (error) {
        setChartData(null);
        console.error("Error fetching chart data:", error);
      }
    };
    fetchChartData();
  }, []);

  // Sample data (replace with real data via props)
  const defaultData = {
    labels: [
      "1 Band saw",
      "2 Structural drill",
      "3 Angle line",
      "4 Plasma",
      "5 Laser",
      "7 Press brake",
      "13 Machine Assist",
      "14 Carbon Steel fabrication",
      "16 Hndl/Bndl/Load/Gen manual Lbr",
      "17 Trucking/Delivery",
    ],
    datasets: [
      {
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderWidth: 0,
      },
    ],
  };

  const finalChartData = chartData || defaultData;

  // Generate colors automatically if not defined
  if (!finalChartData.datasets[0].backgroundColor) {
    finalChartData.datasets[0].backgroundColor = generateColors(
      finalChartData.labels.length
    );
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Disable Chart.js legend to use a custom one
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.label}: ${context.parsed} hours`;
          },
        },
      },
    },
    cutout: "65%", // Make the chart a donut (ring)
  };

  return (
    <div>
      <Row>
        {/* Custom legend — 3 columns */}
        <Col xs="12" md="8" lg="8">
          <h4 className="mb-4">{title}</h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "18px 40px",
              fontSize: "15px",
              alignItems: "start",
            }}
          >
            {finalChartData.labels.map((label, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                {/* Left column: color + label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor:
                        finalChartData.datasets[0].backgroundColor[index],
                      flexShrink: 0,
                    }}
                  />
                  <span>{label}</span>
                </div>

                {/* Right column: value with opacity */}
                <span style={{ opacity: 0.6, textAlign: "right" }}>
                  {finalChartData.datasets[0].data[index]}
                </span>
              </div>
            ))}
          </div>
        </Col>

        {/* Chart */}
        <Col
          xs="12"
          md="4"
          lg="4"
          className="pr-0"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "250px",
              position: "relative",
              aspectRatio: "1",
            }}
          >
            <Doughnut data={finalChartData} options={chartOptions} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default LaborCodeChart;
